import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import './CookieBanner.css';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true - required for basic functionality
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // Initialize analytics/tracking here if accepted
    if (consentData.analytics) {
      // Initialize Google Analytics or other analytics
      console.log('Analytics cookies accepted');
    }
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true, // Always required
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleCustomizeSettings = () => {
    const consentData = {
      ...cookiePreferences,
      necessary: true, // Always required
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // Initialize services based on preferences
    if (consentData.analytics) {
      console.log('Analytics cookies accepted');
    }
  };

  const handlePreferenceChange = (type, value) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-banner-overlay">
      <div className="cookie-banner">
        <div className="cookie-banner-header">
          <div className="cookie-banner-icon">
            <Cookie size={24} />
          </div>
          <h3>We value your privacy</h3>
          <button 
            className="cookie-banner-close"
            onClick={() => setIsVisible(false)}
            aria-label="Close cookie banner"
          >
            <X size={20} />
          </button>
        </div>

        <div className="cookie-banner-content">
          <p>
            We use cookies to enhance your browsing experience, serve personalized content, 
            and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
          </p>
          
          {showDetails && (
            <div className="cookie-details">
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.necessary}
                      disabled
                      aria-label="Necessary cookies (required)"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>Necessary Cookies</h4>
                    <span className="cookie-required">(Required)</span>
                  </div>
                </div>
                <p>These cookies are essential for the website to function properly and cannot be disabled.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      aria-label="Analytics cookies"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>Analytics Cookies</h4>
                  </div>
                </div>
                <p>Help us understand how visitors interact with our website by collecting information anonymously.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.functional}
                      onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                      aria-label="Functional cookies"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>Functional Cookies</h4>
                  </div>
                </div>
                <p>Enable enhanced functionality and personalization, such as remembering your preferences.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      aria-label="Marketing cookies"
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                  <div className="cookie-category-info">
                    <h4>Marketing Cookies</h4>
                  </div>
                </div>
                <p>Used to track visitors across websites to display relevant and engaging advertisements.</p>
              </div>
            </div>
          )}
        </div>

        <div className="cookie-banner-actions">
          <div className="cookie-banner-links">
            <button 
              className="cookie-details-toggle"
              onClick={() => setShowDetails(!showDetails)}
            >
              <Settings size={16} />
              {showDetails ? 'Hide Details' : 'Customize Settings'}
            </button>
            <a href="/privacy" className="cookie-policy-link" target="_blank" rel="noopener noreferrer">
              <Shield size={16} />
              Privacy Policy
            </a>
          </div>
          
          <div className="cookie-banner-buttons">
            <button 
              className="cookie-btn cookie-btn-reject"
              onClick={handleRejectAll}
            >
              Reject All
            </button>
            {showDetails && (
              <button 
                className="cookie-btn cookie-btn-save"
                onClick={handleCustomizeSettings}
              >
                Save Preferences
              </button>
            )}
            <button 
              className="cookie-btn cookie-btn-accept"
              onClick={handleAcceptAll}
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner; 